// INSPECT & ASK — the layer over the page while inspecting
// (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1; the engine is
// functions/aiInspect.js). Mounted at the shell root (app.js) and only while
// the mode is on (`if:` — its document listeners exist only then):
//
//   InspectBox   the outline around the region under the pointer (or the one
//                the arrow keys reached): the house focus ring on a `hover`
//                wash, the card radius, and a tag with the region's name
//   InspectHint  what a click does and the way out — the composer's own glass
//                capsule above the composer, role=status, focused on entry so
//                the keys reach the mode; Cancel leaves
//
// It never moves the page: a fixed overlay that takes no pointer itself (the
// hint excepted). Every press on the page while inspecting belongs to the
// mode (aiInspectSwallow in the capture phase), so a widget never starts a
// drag and a row never opens its record. Motion: the outline glides between
// regions at the house step — and lands at once under reduced motion (the
// brand reset).
export const AiInspectLayer = {
  if: (el, s) => !!(s.root && s.root._aiInspect),
  // FA515: the gate and the outline read root keys behind a guard — declared,
  // so a short-circuited run still re-fires when they change.
  ifDeps: [(el, s) => s.root && s.root._aiInspect],
  position: 'fixed',
  inset: '0',
  // Over the composer (1400) and the page sheet (1300), under every modal
  // (2000) and dropdown layer.
  zIndex: '1450',
  pointerEvents: 'none',

  onDocumentPointermove: { passive: true, handler: (e, el) => el.call('aiInspectHover', e) },
  onDocumentPointerdown: { capture: true, handler: (e, el) => el.call('aiInspectSwallow', e) },
  onDocumentClick: { capture: true, handler: (e, el) => el.call('aiInspectClick', e) },
  onDocumentKeydown: { capture: true, handler: (e, el) => el.call('aiInspectKey', e) },
  onDocumentScroll: {
    capture: true,
    passive: true,
    handler: (e, el) => el.call('aiInspectRefresh')
  },
  onWindowResize: (e, el) => el.call('aiInspectRefresh'),

  InspectBox: {
    hide: (el, s) => !(s.root && s.root._aiInspectBox),
    hideDeps: [(el, s) => s.root && s.root._aiInspectBox],
    styleDeps: [(el, s) => s.root && s.root._aiInspectBox],
    position: 'absolute',
    boxSizing: 'border-box',
    round: 'radiusCard',
    background: 'hover',
    shadow: 'focusRing',
    pointerEvents: 'none',
    transition:
      'top A defaultBezier, left A defaultBezier, width A defaultBezier, height A defaultBezier',
    // The region's measured box (viewport px — a measurement, not a design
    // value).
    style: (el, s) => {
      const b = (s.root && s.root._aiInspectBox) || {}
      return {
        top: (b.y || 0) + 'px',
        left: (b.x || 0) + 'px',
        width: (b.w || 0) + 'px',
        height: (b.h || 0) + 'px'
      }
    },

    // The region's name over its top edge — inside the box when the region
    // starts at the top of the screen.
    InspectTag: {
      position: 'absolute',
      left: '0',
      flow: 'x',
      align: 'center',
      gap: 'X',
      maxWidth: '100%',
      boxSizing: 'border-box',
      padding: 'X Z',
      round: 'radiusPill',
      background: 'highlight',
      color: 'highlight-reversed',
      fontSize: 'Z',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      transformDeps: [(el, s) => s.root && s.root._aiInspectBox],
      transform: (el, s) => {
        const b = (s.root && s.root._aiInspectBox) || {}
        return (b.y || 0) < 48 ? 'translateY(var(--spacing-Y))' : 'translateY(-115%)'
      },
      Icon: { name: 'lnInspect', boxSize: 'iconSm', flexShrink: '0', ariaHidden: 'true' },
      TagText: {
        tag: 'span',
        textDeps: [(el, s) => s.root && s.root._aiInspectBox],
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        text: (el, s) => {
          const b = (s.root && s.root._aiInspectBox) || {}
          const ask = el.call('polyglot', 'workspace.aiInspect.pick')
          return b.label ? b.label + ' · ' + ask : ask
        }
      }
    }
  },

  // The way out, said once and always reachable. Above the composer (its
  // measured top, --prompt-clear), centred.
  InspectHint: {
    role: 'status',
    ariaLive: 'polite',
    tabindex: '-1',
    position: 'absolute',
    left: '50%',
    // --prompt-clear is the shell's registered measure of the composer's top
    // (app.js @property, always defined): the hint sits one Z above it.
    bottom: 'calc(var(--prompt-clear) + var(--spacing-Z))',
    transform: 'translateX(-50%)',
    pointerEvents: 'auto',
    flow: 'x',
    align: 'center',
    gap: 'Z',
    maxWidth: 'calc(100vw - var(--spacing-B))',
    boxSizing: 'border-box',
    padding: 'Y Y Y Z1',
    round: 'radiusPill',
    theme: 'search-glass',
    borderWidth: '0',
    shadow: 'overlay',
    fontSize: 'Z1',
    color: 'title',
    animation: 'fadeInUpShort',
    animationDuration: 'B',
    '@reduceTransparency': { theme: 'dialog', backdropFilter: 'none' },
    ':focus-visible': { shadow: 'focusRing' },

    Plate: {
      ariaHidden: 'true',
      display: 'flex',
      align: 'center center',
      flexShrink: '0',
      boxSize: 'glyphPlateXs',
      round: 'radiusPill',
      background: 'highlight',
      color: 'title-reversed',
      Icon: { name: 'lnInspect', boxSize: 'iconSm' }
    },
    HintText: {
      tag: 'span',
      minWidth: '0',
      text: (el) => el.call('polyglot', 'workspace.aiInspect.hint')
    },
    Keys: {
      flow: 'x',
      align: 'center',
      gap: 'X',
      flexShrink: '0',
      ariaHidden: 'true',
      '@mobileL': { display: 'none' },
      AppKeycap: { text: 'Esc' }
    },
    AppCapsuleBtnGhost: {
      compact: true,
      flexShrink: '0',
      text: (el) => el.call('polyglot', 'common.action.cancel'),
      onClick: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('aiInspectStop')
      }
    }
  }
}
